import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Topbar from '../components/layout/Topbar';
import EmptyState from '../components/shared/EmptyState';
import Input from '../components/shared/Input';
import Pagination from '../components/shared/Pagination';
import { useToast } from '../components/shared/Toast';
import api from '../services/api';
import type { Task } from '../types';

export default function Tasks(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/tasks', {
        params: { ...filters, page: currentPage }
      });
      const data = response.data as { items?: Task[]; total_pages?: number } | Task[]
      if (Array.isArray(data)) {
        setTasks(data);
        setTotalPages(1);
      } else {
        setTasks(data.items ?? []);
        setTotalPages(data.total_pages ?? 1);
      }
    } catch (_error) {
      toast.error('Gorevler yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, toast]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ff4444';
      case 'high': return '#ffaa00';
      case 'medium': return '#00d4ff';
      case 'low': return '#666';
      default: return '#666';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#00ff88';
      case 'in_progress': return '#00d4ff';
      case 'review': return '#ffaa00';
      case 'open': return '#666';
      default: return '#666';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>      <div style={{ flex: 1, overflow: 'auto' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h1>Gorevler</h1>

            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <Input
                type="text"
                name="search"
                placeholder="Gorev ara..."
                value={filters.search}
                onChange={handleFilterChange}
              />
              <Input
                as="select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                options={[
                  { value: '', label: 'Tum Durumlar' },
                  { value: 'open', label: 'Acik' },
                  { value: 'in_progress', label: 'Devam Ediyor' },
                  { value: 'review', label: 'Incelemede' },
                  { value: 'completed', label: 'Tamamlandi' }
                ]}
              />
              <Input
                as="select"
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
                options={[
                  { value: '', label: 'Tum Oncelikler' },
                  { value: 'urgent', label: 'Acil' },
                  { value: 'high', label: 'Yuksek' },
                  { value: 'medium', label: 'Orta' },
                  { value: 'low', label: 'Dusuk' }
                ]}
              />
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>Yukleniyor...</div>
            ) : tasks.length === 0 ? (
              <EmptyState
                title="Gorev Bulunamadi"
                message="Filtreleri degistirerek tekrar deneyin"
              />
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {tasks.map(task => (
                    <div
                      key={task.id}
                      className="task-card"
                      onClick={() => navigate(`/task/${task.id}`)}
                    >
                      <div className="task-header">
                        <h3>{task.title}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <span
                            className="badge"
                            style={{ background: getPriorityColor(task.priority ?? 'medium') }}
                          >
                            {task.priority ?? 'medium'}
                          </span>
                          <span
                            className="badge"
                            style={{ background: getStatusColor(task.status) }}
                          >
                            {task.status}
                          </span>
                        </div>
                      </div>
                      <p className="task-description">{task.description}</p>
                      <div className="task-footer">
                        <span className="task-reward">{task.reward} FLZ</span>
                        {task.deadline && (
                          <span className="task-deadline">
                            Son: {new Date(task.deadline).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
